import {observable, toJS} from 'mobx';
import uniqid from 'uniqid';
import {
    BUILDING_BLOCK_TYPES,
    BuildingBlockType,
    DiagramBuildingBlock,
    DiagramTableBuildingBlock,
    MergedBuildingBlockInstructions,
    NamedDateRangeWithPreviewDateRange,
    PageBreakBuildingBlock,
    PageLayout,
    Report,
    ReportBuildingBlock,
    ReportEditing,
    TableBuildingBlock,
    TextBuildingBlock,
    ToggleableSelectorTableColumnDef,
} from '../types/Report';
import {MessageProvider} from '../../i18n/i18n';
import {DateRange} from '../../stores/TimeIntervalStore';
import {
    BookmarkItem,
    HistoryItem,
    isSerializedLineDiagramRequest,
    MemoryBarTab,
    SerializedChannelDiagramRequest,
    SerializedLineDiagramRequest,
} from '../../stores/MemoryBarStore';
import _ from 'lodash';
import {AnalyticsTab} from '../../stores/store';
import {DataKey, NoneKPIColumns, SelectorTableColumnDef} from '../../stores/GeneralSelectorTableStore';
import {DescriptiveKPIAdditions, KPIGroupKey, ProductKPI, SearchtermKPI} from '../../kpi';
import {
    DiagramInstruction,
    isDiagramInstruction,
    isProductDiagramInstruction,
    isSearchDiagramInstruction,
    ProductDiagramInstruction,
    SearchDiagramInstruction,
} from '../../stores/OdtExportStore';
import {REPORT_DROPABLES} from '../../beautiful-dnd/Droppable';
import {Combine, DraggableLocation} from 'react-beautiful-dnd';
import {expectNever} from '../../types/Utility';
import {DateUtils} from '../../utility/DateUtils';

export const REPORT_VARIABLES = ['date', 'time', 'cw'];

export interface ReportEditorState {
    reportEditing: ReportEditing;
    previewModeEnabled: boolean;
    isHistoryItemBlockName: (blockName: string) => boolean;
    isBookmarkItemBlockName: (blockName: string) => boolean;
    addBuildingBlockToPaperSheet: (buildingBlockType: BuildingBlockType, index: number) => void;
    moveReportBuildingBlockOnPaperSheet: (sourceIndex: number, destinationIndex: number) => void;
    addHistoryItemToPaperSheet: (memoryBlockName: string, memoryBlockIndex: number, idDestinationBlock: string, destinationIndex: number) => void;
    addBookmarkItemToPaperSheet: (memoryBlockName: string, memoryBlockIndex: number, idDestinationBlock: string, destinationIndex: number) => void;
    addHistoryItemToBuildingBlock: (memoryBlockName: string, memoryBlockIndex: number, idDestinationBlock: string) => void;
    addBookmarkItemToBuildingBlock: (memoryBlockName: string, memoryBlockIndex: number, idDestinationBlock: string) => void;
    memoryBarIsOpenThroughReportView: () => boolean;
    closeReportEditor: () => void;
    changeBuildingBlockType: (idBuildingBlock: string, newBlockType: BuildingBlockType) => void;
    addNamedDateRange: () => void;
    renameNamedDateRange: (idNamedDateRange: string, newName: string) => void;
    removeNamedDateRange: (idNamedDateRange: string) => void;
    setPreviewDateRange: (idNamedDateRange: string, dateRange: DateRange) => void;
    setPageLayout: (pageLayout: PageLayout) => void;
    togglePreviewMode: () => void;
    checkIfIdBelongsToBuildingBlock: (id?: string, defaultValue?: boolean) => boolean;
    changeSelectedNamedDateRange: (idBuildingBlock: string, dateIdBundle: DateIdBundle) => void;
    updateTableColumnVisibility: (idTableBuildingBlock: string, dataKey: DataKey, visibility: boolean) => void;
    updateLineLimitForTableBlock: (idTableBuildingBlock: string, lineNumber: number) => void;
    getMergedBuildingBlockInstructions: (buildingBlock: ReportBuildingBlock) => MergedBuildingBlockInstructions;
    removeDiagramTraitFromBuildingBlock: (idBuildingBlock: string, diagramTrait: DiagramInstruction) => void;
    removeDiagramCompareTraitFromBuildingBlock: (idBuildingBlock: string) => void;
    removeTableTraitFromBuildingBlock: (idBuildingBlock: string, traitKey: string, traitToRemove: any) => void;
    openReportEditor: (reportEditing?: ReportEditing) => void;
    editReport: (report: Report) => void;
    dropEvents: (source: DraggableLocation, destination?: DraggableLocation | null, combine?: Combine | null) => void;
    dragStartEvents: (droppableId: string) => void;
    allowBlockCombining: boolean;
    reportBuildingBlocksAreEmpty: (buildingBlocks: ReportBuildingBlock[]) => boolean;
    hasSearchProductMixedBuildingBlocks: (reportBlocks: ReportBuildingBlock[]) => boolean;
    hasToManyChannels: (reportBlocks: ReportBuildingBlock[]) => boolean;
    hasToManySearches: (reportBlocks: ReportBuildingBlock[]) => boolean;
    hasToManySortings: (reportBlocks: ReportBuildingBlock[]) => boolean;
    VISIBLE_DEFAULT_COLUMNS: {[pageLayout in PageLayout]: {[analyticsTab in AnalyticsTab]?: DataKey[]}};
    saveReport: (force?: boolean) => void;
    isSaving: boolean;
    saveConflict?: SaveConflict;
    renameAndSaveAsNewAfterConflict: (reportName: string) => void;
}

interface ReportStoreDependencies {
    setSidebarOpen: (sidebarOpen: boolean) => void;
    messageProvider: MessageProvider;
    getMainDateRange: () => DateRange;
    getActiveAnalyticsTab: () => AnalyticsTab;
    setTargetCurrency: (targetCurrency: string | undefined) => void;
    getTableColumnListPerAnalyticsTab: (analyticsTab: AnalyticsTab) => SelectorTableColumnDef[];
    getHistoryItems: () => HistoryItem[];
    getBookmarkItems: () => BookmarkItem[];
    getAvailableReports: () => Report[];
    getTableColumnListWithChannelPerAnalyticsTab: (analyticsTab: AnalyticsTab) => SelectorTableColumnDef[];
    getTargetCurrency: () => string | undefined;
    saveNewReport: (report: Report) => Promise<void>;
    saveExistingReport: (report: Report, force?: boolean) => Promise<void>;
}

export enum ReportError {
    ReportNameMissing,
    ReportNameAlreadyUsed,
    ReportEmpty,
    ProductAndSearchDataMixed,
    ToManyChannels,
    ToManySearches,
    ToManySortings,
}

export interface DateIdBundle {
    idDate?: string;
    idDateCompare?: string;
}

export interface SaveConflict {
    changedOnDisk: boolean;
    showRenameDialog: boolean;
}

export default class ReportEditorStore implements ReportEditorState {
    @observable public reportEditing: ReportEditing = ReportEditorStore.reportEditingDefault();
    @observable public previewModeEnabled = false;
    @observable public allowBlockCombining = true;
    @observable public isSaving = false;
    @observable public saveConflict?: SaveConflict;

    public VISIBLE_DEFAULT_COLUMNS = {
        [PageLayout.LANDSCAPE]: {
            [AnalyticsTab.SearchView]: [
                NoneKPIColumns.RowNumber,
                NoneKPIColumns.Channel,
                KPIGroupKey.Searchterm,
                SearchtermKPI.SearchCount,
                SearchtermKPI.ClickCount,
                SearchtermKPI.UniqueClickCount,
                SearchtermKPI.CartCount,
                SearchtermKPI.CheckoutCount,
                SearchtermKPI.SalesFromSearches,
                SearchtermKPI.ClickRate,
                SearchtermKPI.ClickToCartRate,
                SearchtermKPI.ClickToBuyRate,
                SearchtermKPI.CartToCheckoutRate,
                SearchtermKPI.UniqueClickRate,
                SearchtermKPI.SalesFromSearchesPerSearch,
                SearchtermKPI.SalesFromSearchesPerClick,
            ],
            [AnalyticsTab.ProductView]: [
                NoneKPIColumns.RowNumber,
                NoneKPIColumns.Channel,
                DescriptiveKPIAdditions.Title,
                ProductKPI.ClickCount,
                ProductKPI.AsnClickCount,
                ProductKPI.NaviClickCount,
                ProductKPI.UniqueClickCount,
                ProductKPI.CartCount,
                ProductKPI.ClickToCartRate,
                ProductKPI.CheckoutCount,
                ProductKPI.CartToCheckoutRate,
                ProductKPI.Sales,
                ProductKPI.SalesPerClick,
            ],
        },
        [PageLayout.PORTRAIT]: {
            [AnalyticsTab.SearchView]: [
                NoneKPIColumns.RowNumber,
                NoneKPIColumns.Channel,
                KPIGroupKey.Searchterm,
                SearchtermKPI.SearchCount,
                SearchtermKPI.ClickCount,
                SearchtermKPI.UniqueClickCount,
                SearchtermKPI.CartCount,
                SearchtermKPI.CheckoutCount,
                SearchtermKPI.ClickRate,
                SearchtermKPI.SalesFromSearches,
                SearchtermKPI.ClickToCartRate,
                SearchtermKPI.ClickToBuyRate,
            ],
            [AnalyticsTab.ProductView]: [
                NoneKPIColumns.RowNumber,
                NoneKPIColumns.Channel,
                DescriptiveKPIAdditions.Title,
                ProductKPI.ClickCount,
                ProductKPI.UniqueClickCount,
                ProductKPI.CartCount,
                ProductKPI.ClickToCartRate,
                ProductKPI.CheckoutCount,
                ProductKPI.CartToCheckoutRate,
                ProductKPI.Sales,
                ProductKPI.SalesPerClick,
            ],
        },
    };

    constructor(private reportStoreDependencies: ReportStoreDependencies) {}

    private static reportEditingDefault(): ReportEditing {
        return {
            editorIsActive: false,
            errors: [],
            name: '',
            blocks: [],
            id: -1,
            layout: PageLayout.LANDSCAPE,
            namedDateRanges: [],
        };
    }

    public renameAndSaveAsNewAfterConflict = (reportName: string) => {
        this.reportEditing.name = reportName;
        this.reportEditing.id = -1;
        this.saveReport();
    };

    public dragStartEvents = (droppableId: string) => {
        if (droppableId === REPORT_DROPABLES.REPORT_PAPERSHEET || droppableId === REPORT_DROPABLES.REPORT_TYPE_BUILDING_BLOCKS) {
            this.allowBlockCombining = false;
        }
    };

    public dropEvents = (source: DraggableLocation, destination?: DraggableLocation | null, combine?: Combine | null): void => {
        this.allowBlockCombining = true;
        if (destination && source) {
            // ReportTypeBuildingBlocks -> PaperSheet
            if (
                source.droppableId === REPORT_DROPABLES.REPORT_TYPE_BUILDING_BLOCKS &&
                destination.droppableId === REPORT_DROPABLES.REPORT_PAPERSHEET
            ) {
                return this.addBuildingBlockToPaperSheet(BUILDING_BLOCK_TYPES[source.index], destination.index);
            }

            // PaperSheet -> PaperSheet
            if (source.droppableId === REPORT_DROPABLES.REPORT_PAPERSHEET && destination.droppableId === REPORT_DROPABLES.REPORT_PAPERSHEET) {
                return this.moveReportBuildingBlockOnPaperSheet(source.index, destination.index);
            }

            // HistoryItem  -> PaperSheet
            if (this.isHistoryItemBlockName(source.droppableId)) {
                return this.addHistoryItemToPaperSheet(source.droppableId, source.index, destination.droppableId, destination.index);
            }

            //BookmarkItem -> PaperSheet
            if (this.isBookmarkItemBlockName(source.droppableId)) {
                return this.addBookmarkItemToPaperSheet(source.droppableId, source.index, destination.droppableId, destination.index);
            }
        }

        if (source && combine) {
            // HistoryItem  -> Some Block
            if (this.isHistoryItemBlockName(source.droppableId)) {
                return this.addHistoryItemToBuildingBlock(source.droppableId, source.index, combine.draggableId);
            }

            //BookmarkItem -> Some Block
            if (this.isBookmarkItemBlockName(source.droppableId)) {
                return this.addBookmarkItemToBuildingBlock(source.droppableId, source.index, combine.draggableId);
            }
        }
    };

    public setPageLayout = (pageLayout: PageLayout) => {
        this.changeBuildingBlockColumnsIfDefault(pageLayout);
        this.reportEditing.layout = pageLayout;
    };

    public closeReportEditor = () => {
        this.reportEditing = this.reportEditingDefault();
        this.reportStoreDependencies.setSidebarOpen(false);
    };

    private reportEditingDefault(): ReportEditing {
        return {
            ...ReportEditorStore.reportEditingDefault(),
            namedDateRanges: [this.generateDefaultNamedDateRange()],
        };
    }

    private generateDefaultNamedDateRange(): NamedDateRangeWithPreviewDateRange {
        return {
            id: uniqid(),
            name: this.reportStoreDependencies.messageProvider('report:dateRange', {dateRangeNumber: this.getFirstUnusedReportRangeNumber()}),
            previewDateRange: this.reportStoreDependencies.getMainDateRange(),
        };
    }

    public openReportEditor = (reportEditing?: ReportEditing) => {
        if (reportEditing) {
            this.reportEditing = reportEditing;
        } else {
            this.reportEditing = {
                ...this.reportEditingDefault(),
                editorIsActive: true,
            };
        }
        this.reportStoreDependencies.setSidebarOpen(true);
    };

    public isHistoryItemBlockName = (blockName: string): boolean => {
        return blockName === REPORT_DROPABLES.MEMORY_BAR + '-' + MemoryBarTab.HISTORY;
    };

    public isBookmarkItemBlockName = (blockName: string): boolean => {
        return blockName === REPORT_DROPABLES.MEMORY_BAR + '-' + MemoryBarTab.FAVORITES;
    };

    private defaultTextBuildingBlock(id?: string): TextBuildingBlock {
        return {
            id: id ? id : uniqid(),
            blockType: BuildingBlockType.TEXT,
            text: '',
            textStyle: {
                italic: false,
                underlined: false,
                bold: false,
                fontSize: 12,
            },
        };
    }

    private getDefaultNamedDateRangeId(index = 0): string {
        const neededDateRanges = this.reportEditing.namedDateRanges.length - index - 1;
        for (let i = neededDateRanges; i < 0; i++) {
            this.addNamedDateRange();
        }
        return this.reportEditing.namedDateRanges[index].id;
    }

    private defaultTableBuildingBlock(id?: string): TableBuildingBlock {
        return {
            blockType: BuildingBlockType.TABLE,
            id: uniqid(),
            memoryItems: [],
            lineLimit: 10,
            columns: [],
            idDate: this.getDefaultNamedDateRangeId(),
        };
    }

    private defaultDiagramBuildingBlock(id?: string, withCompareData?: boolean): DiagramBuildingBlock {
        return {
            blockType: BuildingBlockType.DIAGRAM,
            id: id ? id : uniqid(),
            memoryItems: [],
            idDate: this.getDefaultNamedDateRangeId(),
            idDateCompare: withCompareData ? this.getDefaultNamedDateRangeId(1) : undefined,
        };
    }

    private defaultDiagramTableBuildingBlock(id?: string, withCompareData?: boolean): DiagramTableBuildingBlock {
        return {
            blockType: BuildingBlockType.DIAGRAM_TABLE,
            id: id ? id : uniqid(),
            memoryItems: [],
            columns: [],
            idDate: this.getDefaultNamedDateRangeId(),
            idDateCompare: withCompareData ? this.getDefaultNamedDateRangeId(1) : undefined,
        };
    }

    private defaultPageBreakBuildingBlock(id?: string): PageBreakBuildingBlock {
        return {
            id: id ? id : uniqid(),
            blockType: BuildingBlockType.PAGE_BREAK,
        };
    }

    public moveReportBuildingBlockOnPaperSheet(sourceIndex: number, destinationIndex: number) {
        const {blocks} = this.reportEditing;
        const buildingBlockToMove = blocks[sourceIndex];
        blocks.splice(sourceIndex, 1);
        blocks.splice(destinationIndex, 0, buildingBlockToMove);
        this.reportEditing.blocks = blocks;
    }

    private getDefaultBuildingBlockForBuildingBlockType(
        buildingBlockType: BuildingBlockType,
        idToUse?: string,
        withCompareData?: boolean
    ): ReportBuildingBlock {
        switch (buildingBlockType) {
            case BuildingBlockType.TEXT:
                return this.defaultTextBuildingBlock(idToUse);
            case BuildingBlockType.DIAGRAM:
                return this.defaultDiagramBuildingBlock(idToUse, withCompareData);
            case BuildingBlockType.TABLE:
                return this.defaultTableBuildingBlock(idToUse);
            case BuildingBlockType.DIAGRAM_TABLE:
                return this.defaultDiagramTableBuildingBlock(idToUse, withCompareData);
            case BuildingBlockType.PAGE_BREAK:
                return this.defaultPageBreakBuildingBlock(idToUse);
            default:
                throw Error('Unsupported report building block type ' + buildingBlockType);
        }
    }

    public addBuildingBlockToPaperSheet = (buildingBlockType: BuildingBlockType, index: number): void => {
        this.reportEditing.blocks.splice(index, 0, this.getDefaultBuildingBlockForBuildingBlockType(buildingBlockType));
    };

    public checkIfIdBelongsToBuildingBlock = (id?: string, defaultValue = false): boolean => {
        if (!id) {
            return defaultValue;
        }
        const match = this.getBuildingBlockById(this.reportEditing.blocks, id);
        return !!match;
    };

    private getBuildingBlockById(reportBuildingBlocks: ReportBuildingBlock[], targetId: string): ReportBuildingBlock | undefined {
        let i;
        const l = reportBuildingBlocks.length;
        for (i = 0; i < l; i++) {
            const reportBuildingBlock = reportBuildingBlocks[i];
            if (reportBuildingBlock.id === targetId) {
                return reportBuildingBlock;
            }
        }
        return undefined;
    }

    private addDefaultColumnsForTableBlock(buildingBlock: TableBuildingBlock | DiagramTableBuildingBlock) {
        const {layout} = this.reportEditing;
        const mergedTableData = this.getMergedBuildingBlockInstructions(buildingBlock);
        const activeViews = mergedTableData && mergedTableData.tableInstruction ? mergedTableData.tableInstruction.activeViews : [];
        if (activeViews.length === 1) {
            const analyticsTab = activeViews[0];
            if (analyticsTab === AnalyticsTab.SearchView) {
                if (buildingBlock.columns.length === 0 || !buildingBlock.columns.some((bb) => bb.dataKey === KPIGroupKey.Searchterm)) {
                    buildingBlock.columns = this.reportStoreDependencies
                        .getTableColumnListWithChannelPerAnalyticsTab(analyticsTab)
                        .map((columnDef) => ({
                            ...columnDef,
                            isVisible: this.VISIBLE_DEFAULT_COLUMNS[layout][AnalyticsTab.SearchView].some(
                                (visibleDataKey) => visibleDataKey === columnDef.dataKey
                            ),
                        }));
                }
            } else if (analyticsTab === AnalyticsTab.ProductView) {
                if (buildingBlock.columns.length === 0 || !buildingBlock.columns.some((bb) => bb.dataKey === KPIGroupKey.ProductId)) {
                    buildingBlock.columns = this.reportStoreDependencies
                        .getTableColumnListWithChannelPerAnalyticsTab(analyticsTab)
                        .map((columnDef) => ({
                            ...columnDef,
                            isVisible: this.VISIBLE_DEFAULT_COLUMNS[layout][AnalyticsTab.ProductView].some(
                                (visibleDataKey) => visibleDataKey === columnDef.dataKey
                            ),
                        }));
                }
            } else {
                throw Error('Invalid analyticsTab for TableBuildingBlock, ' + analyticsTab);
            }
        }
    }

    private addMemoryItemToDestinationBlock(movingHistoryItem: HistoryItem, idDestinationBlock: string) {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        const destinationBlock = this.getBuildingBlockById(reportBuildingBlocks, idDestinationBlock);
        if (!destinationBlock) {
            throw Error('Could not find destination block for historyItem drop');
        }

        if (destinationBlock.blockType === BuildingBlockType.DIAGRAM) {
            destinationBlock.memoryItems.push(movingHistoryItem);
        } else if (destinationBlock.blockType === BuildingBlockType.DIAGRAM_TABLE || destinationBlock.blockType === BuildingBlockType.TABLE) {
            destinationBlock.memoryItems.push(movingHistoryItem);
            this.addDefaultColumnsForTableBlock(destinationBlock);
        }

        if (destinationBlock.blockType === BuildingBlockType.DIAGRAM || destinationBlock.blockType === BuildingBlockType.DIAGRAM_TABLE) {
            const mergedInstructions = this.getMergedBuildingBlockInstructions(destinationBlock);
            if (mergedInstructions.diagramInstruction && mergedInstructions.diagramInstruction.compareRangeActive) {
                destinationBlock.idDateCompare = this.getDefaultNamedDateRangeId(1);
            }
        }

        this.reportEditing.blocks = reportBuildingBlocks;
    }

    private addMemoryItemToPaperSheet(movingHistoryItem: HistoryItem, idDestinationBlock: string, destinationIndex: number) {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        const mergedTableInstructions = ReportEditorStore.mergeTableInstructions([movingHistoryItem]);
        const mergedDiagramInstructions = ReportEditorStore.mergeDiagramInstructions([movingHistoryItem]);

        const {searchDiagrams, productDiagrams} = mergedDiagramInstructions.diagramInstruction
            ? mergedDiagramInstructions.diagramInstruction
            : {searchDiagrams: [], productDiagrams: []};
        const {activeViews, channels} = mergedTableInstructions.tableInstruction
            ? mergedTableInstructions.tableInstruction
            : {
                  activeViews: [],
                  channels: [],
              };

        const compareDiagramActive = mergedDiagramInstructions.diagramInstruction
            ? mergedDiagramInstructions.diagramInstruction.compareRangeActive
                ? mergedDiagramInstructions.diagramInstruction.compareRangeActive
                : false
            : false;

        let newBuildingBlockType;

        if (searchDiagrams.length > 0 || productDiagrams.length > 0) {
            newBuildingBlockType = BuildingBlockType.DIAGRAM_TABLE;
        } else if (activeViews.length > 0 && channels.length > 0) {
            newBuildingBlockType = BuildingBlockType.TABLE;
        }

        if (newBuildingBlockType) {
            const newDiagramTableBlock = this.getDefaultBuildingBlockForBuildingBlockType(newBuildingBlockType, undefined, compareDiagramActive);
            if (newDiagramTableBlock.blockType === BuildingBlockType.DIAGRAM_TABLE || newDiagramTableBlock.blockType === BuildingBlockType.TABLE) {
                newDiagramTableBlock.memoryItems.push(movingHistoryItem);
                this.addDefaultColumnsForTableBlock(newDiagramTableBlock);
            }
            reportBuildingBlocks.splice(destinationIndex, 0, newDiagramTableBlock);
            this.reportEditing.blocks = reportBuildingBlocks;
        }
    }

    public addHistoryItemToPaperSheet = (memoryBlockName: string, memoryBlockIndex: number, idDestinationBlock: string, destinationIndex: number) => {
        this.addMemoryItemToPaperSheet(this.reportStoreDependencies.getHistoryItems()[memoryBlockIndex], idDestinationBlock, destinationIndex);
    };

    public addBookmarkItemToPaperSheet = (
        memoryBlockName: string,
        memoryBlockIndex: number,
        idDestinationBlock: string,
        destinationIndex: number
    ) => {
        this.addMemoryItemToPaperSheet(this.reportStoreDependencies.getBookmarkItems()[memoryBlockIndex], idDestinationBlock, destinationIndex);
    };

    public addHistoryItemToBuildingBlock = (memoryBlockName: string, memoryBlockIndex: number, idDestinationBlock: string) => {
        this.addMemoryItemToDestinationBlock(this.reportStoreDependencies.getHistoryItems()[memoryBlockIndex], idDestinationBlock);
    };

    public addBookmarkItemToBuildingBlock = (memoryBlockName: string, memoryBlockIndex: number, idDestinationBlock: string) => {
        this.addMemoryItemToDestinationBlock(this.reportStoreDependencies.getBookmarkItems()[memoryBlockIndex], idDestinationBlock);
    };

    private diagramItemsMatchTrait(
        memoryDiagramItem: SerializedLineDiagramRequest | SerializedChannelDiagramRequest,
        compareTrait: DiagramInstruction
    ) {
        if (isSearchDiagramInstruction(compareTrait)) {
            return (
                (isSerializedLineDiagramRequest(memoryDiagramItem) ? memoryDiagramItem.searchtermOrProductId === compareTrait.searchterm : true) &&
                memoryDiagramItem.channel === compareTrait.channel &&
                memoryDiagramItem.kpi === compareTrait.kpi
            );
        } else if (isProductDiagramInstruction(compareTrait)) {
            return (
                (isSerializedLineDiagramRequest(memoryDiagramItem) ? memoryDiagramItem.searchtermOrProductId === compareTrait.productId : true) &&
                memoryDiagramItem.channel === compareTrait.channel &&
                memoryDiagramItem.kpi === compareTrait.kpi
            );
        } else if (isDiagramInstruction(memoryDiagramItem)) {
            return memoryDiagramItem.channel === compareTrait.channel && memoryDiagramItem.kpi === compareTrait.kpi;
        } else {
            return false;
        }
    }

    public removeDiagramCompareTraitFromBuildingBlock = (idBuildingBlock: string): void => {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        const buildingBlock = this.getBuildingBlockById(reportBuildingBlocks, idBuildingBlock);
        if (!buildingBlock) {
            throw Error('Building block not found');
        }
        if (buildingBlock.blockType === BuildingBlockType.DIAGRAM || buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE) {
            buildingBlock.memoryItems = buildingBlock.memoryItems.map((memoryItem) => ({
                ...memoryItem,
                allowDateCompareRange: false,
            }));
            delete buildingBlock['idDateCompare'];
        }

        this.reportEditing.blocks = reportBuildingBlocks;
    };

    public removeDiagramTraitFromBuildingBlock = (idBuildingBlock: string, diagramTrait: DiagramInstruction) => {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        const buildingBlock = this.getBuildingBlockById(reportBuildingBlocks, idBuildingBlock);

        if (!buildingBlock) {
            throw Error('Building block not found');
        }

        if (buildingBlock.blockType === BuildingBlockType.DIAGRAM || buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE) {
            buildingBlock.memoryItems = buildingBlock.memoryItems.map((memoryItem) => {
                const activeDiagramItems = memoryItem.activeDiagramItems
                    ? memoryItem.activeDiagramItems
                    : {channelDiagrams: [], diagrams: [], showAbsoluteValues: false};

                return {
                    ...memoryItem,
                    activeDiagramItems: {
                        ...memoryItem.activeDiagramItems,
                        diagrams: activeDiagramItems.diagrams.filter((diagram) => !this.diagramItemsMatchTrait(diagram, diagramTrait)),
                        channelDiagrams: activeDiagramItems.channelDiagrams.filter((diagram) => !this.diagramItemsMatchTrait(diagram, diagramTrait)),
                        showAbsoluteValues: false,
                    },
                };
            });
        }
        this.reportEditing.blocks = reportBuildingBlocks;
    };

    private memoryItemStillUsable(memoryItem: Partial<HistoryItem>): boolean {
        return !!memoryItem.analyticsTab;
    }

    public removeTableTraitFromBuildingBlock = (idBuildingBlock: string, traitKey: string, traitToRemove: any) => {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        const buildingBlock = this.getBuildingBlockById(reportBuildingBlocks, idBuildingBlock);
        if (!buildingBlock) {
            throw Error('Building block not found');
        }

        if (
            buildingBlock.blockType === BuildingBlockType.DIAGRAM ||
            buildingBlock.blockType === BuildingBlockType.TABLE ||
            buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE
        ) {
            buildingBlock.memoryItems = buildingBlock.memoryItems.reduce((memoryItems: Array<Partial<HistoryItem>>, memoryItem) => {
                if (traitKey === 'activeTableSort') {
                    if (_.isEqual(memoryItem.activeTableSort, traitToRemove)) {
                        delete memoryItem['activeTableSort'];
                    }
                }
                if (traitKey === 'channel' || traitKey === 'activeSearch' || traitKey === 'analyticsTab') {
                    if (memoryItem[traitKey] === traitToRemove) {
                        delete memoryItem[traitKey];
                    }
                }

                if (traitKey === 'activeFilters' && memoryItem.activeFilters && typeof traitToRemove === 'object') {
                    memoryItem.activeFilters = memoryItem.activeFilters.filter(
                        (activeFilter) =>
                            activeFilter.attribute !== traitToRemove.attribute &&
                            activeFilter.comparator !== traitToRemove.comparator &&
                            activeFilter.value !== traitToRemove.value
                    );
                }

                if (this.memoryItemStillUsable(memoryItem)) {
                    memoryItems.push(memoryItem);
                }
                return memoryItems;
            }, []);
        }
        if (buildingBlock.blockType === BuildingBlockType.TABLE) {
            this.addDefaultColumnsForTableBlock(buildingBlock);
        }

        this.reportEditing.blocks = reportBuildingBlocks;
    };

    public reportBuildingBlocksAreEmpty = (buildingBlocks: ReportBuildingBlock[]): boolean => {
        if (buildingBlocks.length === 0) {
            return true;
        }
        const emptyBlocks = buildingBlocks.filter((buildingBlock) => {
            if (buildingBlock.blockType === BuildingBlockType.TABLE) {
                const mergedBuildingBlockInstruction = this.getMergedBuildingBlockInstructions(buildingBlock);
                const {channels, activeViews} = mergedBuildingBlockInstruction.tableInstruction
                    ? mergedBuildingBlockInstruction.tableInstruction
                    : {channels: [], activeViews: []};
                return channels.length === 0 || activeViews.length === 0;
            } else if (buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE || buildingBlock.blockType === BuildingBlockType.DIAGRAM) {
                const mergedBuildingBlockInstructions = this.getMergedBuildingBlockInstructions(buildingBlock);
                const {productDiagrams, searchDiagrams} = mergedBuildingBlockInstructions.diagramInstruction
                    ? mergedBuildingBlockInstructions.diagramInstruction
                    : {productDiagrams: [], searchDiagrams: []};
                return productDiagrams.length === 0 && searchDiagrams.length === 0;
            }
            return false;
        });

        return emptyBlocks.length > 0;
    };

    public hasSearchProductMixedBuildingBlocks = (reportBlocks: ReportBuildingBlock[]): boolean => {
        return reportBlocks.some((reportBlock) => {
            if (reportBlock.blockType === BuildingBlockType.DIAGRAM || reportBlock.blockType === BuildingBlockType.DIAGRAM_TABLE) {
                const mergedDiagramInstructions = ReportEditorStore.mergeDiagramInstructions(reportBlock.memoryItems);
                const {searchDiagrams, productDiagrams} = mergedDiagramInstructions.diagramInstruction
                    ? mergedDiagramInstructions.diagramInstruction
                    : {productDiagrams: [], searchDiagrams: []};
                return productDiagrams.length > 0 && searchDiagrams.length > 0;
            } else if (reportBlock.blockType === BuildingBlockType.TABLE) {
                const mergedTableInstructions = ReportEditorStore.mergeTableInstructions(reportBlock.memoryItems);
                const {activeViews} = mergedTableInstructions.tableInstruction ? mergedTableInstructions.tableInstruction : {activeViews: []};
                return activeViews.length > 1;
            }
            return false;
        });
    };

    public hasToManyChannels = (reportBlocks: ReportBuildingBlock[]): boolean => {
        return reportBlocks.some((reportBlock) => {
            if (reportBlock.blockType === BuildingBlockType.TABLE) {
                const mergedTableInstructions = ReportEditorStore.mergeTableInstructions(reportBlock.memoryItems);
                const {channels} = mergedTableInstructions.tableInstruction ? mergedTableInstructions.tableInstruction : {channels: []};
                return channels.length > 1;
            }
            return false;
        });
    };

    public hasToManySearches = (reportBlocks: ReportBuildingBlock[]): boolean => {
        return reportBlocks.some((reportBlock) => {
            if (reportBlock.blockType === BuildingBlockType.TABLE) {
                const mergedTableInstructions = ReportEditorStore.mergeTableInstructions(reportBlock.memoryItems);
                const {searches} = mergedTableInstructions.tableInstruction ? mergedTableInstructions.tableInstruction : {searches: []};
                return searches.length > 1;
            }
            return false;
        });
    };

    public hasToManySortings = (reportBlocks: ReportBuildingBlock[]): boolean => {
        return reportBlocks.some((reportBlock) => {
            if (reportBlock.blockType === BuildingBlockType.TABLE) {
                const mergedTableInstructions = ReportEditorStore.mergeTableInstructions(reportBlock.memoryItems);
                const {sortings} = mergedTableInstructions.tableInstruction ? mergedTableInstructions.tableInstruction : {sortings: []};
                return sortings.length > 1;
            }
            return false;
        });
    };

    private extractVariablesFromTextBuildingBlocks(reportBuildingBlocks: ReportBuildingBlock[]): string[] {
        return _.uniq(
            reportBuildingBlocks.reduce((variables: string[], reportBuildingBlock) => {
                if (reportBuildingBlock.blockType === BuildingBlockType.TEXT) {
                    const variableRegex = new RegExp('\\${(.*?)}', 'g');
                    let match = variableRegex.exec(reportBuildingBlock.text);
                    while (match !== null) {
                        if (match[1]) {
                            const nameMatch = match[1].match(/^(.*?):/);
                            if (!REPORT_VARIABLES.includes(nameMatch ? nameMatch[1] : match[1])) {
                                variables.push(match[1]);
                            }
                        }
                        match = variableRegex.exec(reportBuildingBlock.text);
                    }
                }
                return variables;
            }, [])
        );
    }

    public editReport = (report: Report) => {
        this.reportStoreDependencies.setTargetCurrency(report.currency);
        this.openReportEditor({
            ...ReportEditorStore.reportEditingDefault(),
            ...report,
            namedDateRanges: report.dates.map((namedDateRange) => ({
                ...namedDateRange,
                previewDateRange: this.reportStoreDependencies.getMainDateRange(),
            })),
            id: report.id,
            editorIsActive: true,
        });
    };

    public updateLineLimitForTableBlock = (idTableBuildingBlock: string, lineNumber: number) => {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        const buildingBlock = this.getBuildingBlockById(reportBuildingBlocks, idTableBuildingBlock);

        if (!buildingBlock) {
            throw Error('Building block not found');
        }

        if (buildingBlock.blockType === BuildingBlockType.TABLE) {
            buildingBlock.lineLimit = lineNumber;
        } else {
            throw Error('Cannot update line limit to a none table building block, block is ' + buildingBlock.blockType);
        }
        this.reportEditing.blocks = reportBuildingBlocks;
    };

    public updateTableColumnVisibility = (idTableBuildingBlock: string, dataKey: DataKey, visibility: boolean) => {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        const buildingBlock = this.getBuildingBlockById(reportBuildingBlocks, idTableBuildingBlock);

        if (!buildingBlock) {
            throw Error('Building block not found');
        }

        if (buildingBlock.blockType === BuildingBlockType.TABLE || buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE) {
            buildingBlock.columns = buildingBlock.columns.map((visibleColum) => ({
                ...visibleColum,
                isVisible: visibleColum.dataKey === dataKey ? visibility : visibleColum.isVisible,
            }));
        } else {
            throw Error('Cannot update line limit to a none table building block, block is ' + buildingBlock.blockType);
        }

        this.reportEditing.blocks = reportBuildingBlocks;
    };

    public memoryBarIsOpenThroughReportView = (): boolean => {
        return this.reportEditing.editorIsActive && this.reportStoreDependencies.getActiveAnalyticsTab() === AnalyticsTab.ReportView;
    };

    public changeBuildingBlockType = (idBuildingBlock: string, newBlockType: BuildingBlockType) => {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        const buildingBlock = this.getBuildingBlockById(reportBuildingBlocks, idBuildingBlock);

        if (!buildingBlock) {
            throw Error('Building block not found');
        }
        const buildingBlockHasMemoryItems =
            buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE ||
            buildingBlock.blockType === BuildingBlockType.DIAGRAM ||
            buildingBlock.blockType === BuildingBlockType.TABLE;
        if (buildingBlockHasMemoryItems && newBlockType === BuildingBlockType.TEXT) {
            const defaultTextBuildingBlock = this.defaultTextBuildingBlock(buildingBlock.id);
            Object.keys(defaultTextBuildingBlock).forEach((defaultTextKey) => {
                buildingBlock[defaultTextKey] = defaultTextBuildingBlock[defaultTextKey];
            });
        } else if (
            buildingBlockHasMemoryItems &&
            (newBlockType === BuildingBlockType.TABLE ||
                newBlockType === BuildingBlockType.DIAGRAM ||
                newBlockType === BuildingBlockType.DIAGRAM_TABLE)
        ) {
            if (newBlockType === BuildingBlockType.TABLE || newBlockType === BuildingBlockType.DIAGRAM_TABLE) {
                const defaultTableBlock = this.defaultTableBuildingBlock(buildingBlock.id);
                buildingBlock.blockType = newBlockType;
                buildingBlock['lineLimit'] = defaultTableBlock.lineLimit;
                buildingBlock['columns'] = defaultTableBlock.columns;
                if (buildingBlock.blockType === BuildingBlockType.TABLE || buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE) {
                    this.addDefaultColumnsForTableBlock(buildingBlock);
                }
            } else {
                buildingBlock.blockType = newBlockType;
            }
        } else {
            const hasCompareDiagrams = (): boolean => {
                if (buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE || buildingBlock.blockType === BuildingBlockType.DIAGRAM) {
                    const mergedInstructions = this.getMergedBuildingBlockInstructions(buildingBlock);
                    const compareActive = mergedInstructions.diagramInstruction ? mergedInstructions.diagramInstruction : {compareRangeActive: false};
                    return compareActive.compareRangeActive || false;
                }
                return false;
            };

            const newDefaultBuildingBlock = this.getDefaultBuildingBlockForBuildingBlockType(newBlockType, buildingBlock.id, hasCompareDiagrams());
            Object.keys(newDefaultBuildingBlock).forEach((defaultBlockKey) => {
                if (defaultBlockKey === 'memoryItems' && buildingBlock['memoryItems']) {
                    return;
                }
                buildingBlock[defaultBlockKey] = newDefaultBuildingBlock[defaultBlockKey];
            });
            if (buildingBlock.blockType === BuildingBlockType.TABLE || buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE) {
                this.addDefaultColumnsForTableBlock(buildingBlock);
            }
        }
        this.reportEditing.blocks = reportBuildingBlocks;
    };

    private getFirstUnusedReportRangeNumber() {
        let dateRangeNumberToTry = 1;
        let dateRangeNumberUnused = false;

        while (!dateRangeNumberUnused) {
            const nameToCheck = this.reportStoreDependencies.messageProvider('report:dateRange', {dateRangeNumber: dateRangeNumberToTry});
            if (!this.reportEditing.namedDateRanges.some((nameDateRange) => nameDateRange.name === nameToCheck)) {
                dateRangeNumberUnused = true;
            } else {
                dateRangeNumberToTry++;
            }
        }
        return dateRangeNumberToTry;
    }

    public addNamedDateRange = () => {
        this.reportEditing.namedDateRanges.push(this.generateDefaultNamedDateRange());
    };

    public renameNamedDateRange = (idNamedDateRange: string, newName: string) => {
        const namedDateRanges = this.reportEditing.namedDateRanges;
        const namedDateRangeToUpdate = namedDateRanges.find((namedDateRange) => namedDateRange.id === idNamedDateRange);
        if (!namedDateRangeToUpdate) {
            throw Error('Could not find named date range for id ' + idNamedDateRange);
        }
        namedDateRangeToUpdate.name = newName;
        this.reportEditing.namedDateRanges = namedDateRanges;
    };

    public removeNamedDateRange = (idNamedDateRange: string) => {
        const indexToRemove = this.reportEditing.namedDateRanges.findIndex((namedDateRange) => namedDateRange.id === idNamedDateRange);
        if (indexToRemove > -1) {
            this.reportEditing.namedDateRanges.splice(indexToRemove, 1);
            this.ensureAtLeastOneNameDateRangeItem();
            this.ensureNamedDateRangesOnBuildingBlocks();
        }
    };

    private ensureNamedDateRangesOnBuildingBlocks() {
        const {namedDateRanges, blocks} = this.reportEditing;
        if (namedDateRanges.length > 0) {
            const defaultNamedDateRange = namedDateRanges[0];
            this.reportEditing.blocks = blocks.map((reportBuildingBlock) => {
                if (
                    reportBuildingBlock.blockType === BuildingBlockType.TABLE ||
                    reportBuildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE ||
                    reportBuildingBlock.blockType === BuildingBlockType.DIAGRAM
                ) {
                    return {
                        ...reportBuildingBlock,
                        idNamedDateRange: namedDateRanges.some((namedDateRange) => namedDateRange.id === reportBuildingBlock.idDate)
                            ? reportBuildingBlock.idDate
                            : defaultNamedDateRange.id,
                    };
                }
                return reportBuildingBlock;
            });
        } else {
            throw Error('Cannot set default namedDateRange value if namedDateRanges is empty');
        }
    }

    private ensureAtLeastOneNameDateRangeItem() {
        if (this.reportEditing.namedDateRanges.length === 0) {
            this.reportEditing.namedDateRanges.push(this.generateDefaultNamedDateRange());
        }
    }

    public changeSelectedNamedDateRange = (idBuildingBlock: string, {idDate, idDateCompare}: DateIdBundle) => {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        const buildingBlock = this.getBuildingBlockById(reportBuildingBlocks, idBuildingBlock);

        if (!buildingBlock) {
            throw Error('Building block not found');
        }

        if (
            buildingBlock.blockType === BuildingBlockType.TABLE ||
            buildingBlock.blockType === BuildingBlockType.DIAGRAM ||
            buildingBlock.blockType === BuildingBlockType.DIAGRAM_TABLE
        ) {
            if (idDate) {
                buildingBlock.idDate = idDate;
            }
            if (idDateCompare && buildingBlock.blockType !== BuildingBlockType.TABLE) {
                buildingBlock.idDateCompare = idDateCompare;
            }
        }
        this.reportEditing.blocks = reportBuildingBlocks;
    };

    public togglePreviewMode = () => {
        this.previewModeEnabled = !this.previewModeEnabled;
    };

    public setPreviewDateRange = (idNamedDateRange: string, dateRange: DateRange) => {
        const namedDateRanges = _.cloneDeep(toJS(this.reportEditing.namedDateRanges));
        const namedDateRangeToUpdate = namedDateRanges.find((namedDateRange) => namedDateRange.id === idNamedDateRange);
        if (!namedDateRangeToUpdate) {
            throw Error('Cannot find namedDateRange to update');
        }
        namedDateRangeToUpdate.previewDateRange = DateUtils.normalizeDateRange(dateRange);
        this.reportEditing.namedDateRanges = namedDateRanges;
    };

    public static mergeDiagramInstructions = (memoryItems: Array<Partial<HistoryItem>>): MergedBuildingBlockInstructions => {
        const diagramInstruction = memoryItems.reduce(
            (mergedDiagrams, memoryItem) => {
                const {channelDiagrams, diagrams} = memoryItem.activeDiagramItems
                    ? memoryItem.activeDiagramItems
                    : {diagrams: [] as SerializedLineDiagramRequest[], channelDiagrams: [] as SerializedChannelDiagramRequest[]};

                if (memoryItem.analyticsTab === AnalyticsTab.SearchView) {
                    return {
                        productDiagrams: mergedDiagrams.productDiagrams,
                        searchDiagrams: _.uniqWith(
                            [
                                ...mergedDiagrams.searchDiagrams,
                                ...channelDiagrams.map(ReportEditorStore.toSearchDiagramInstruction),
                                ...diagrams.map(ReportEditorStore.toSearchDiagramInstruction),
                            ],
                            _.isEqual
                        ),
                        compareRangeActive: mergedDiagrams.compareRangeActive ? true : memoryItem.allowDateCompareRange,
                    };
                } else if (memoryItem.analyticsTab === AnalyticsTab.ProductView) {
                    return {
                        searchDiagrams: mergedDiagrams.searchDiagrams,
                        productDiagrams: _.uniqWith(
                            [
                                ...mergedDiagrams.productDiagrams,
                                ...channelDiagrams.map(ReportEditorStore.toProductDiagramInstruction),
                                ...diagrams.map(ReportEditorStore.toProductDiagramInstruction),
                            ],
                            _.isEqual
                        ),
                        compareRangeActive: mergedDiagrams.compareRangeActive ? true : memoryItem.allowDateCompareRange,
                    };
                } else {
                    throw Error(
                        'Can only merge diagrams for ' +
                            AnalyticsTab.SearchView +
                            ' and ' +
                            AnalyticsTab.ProductView +
                            ', tried ' +
                            memoryItem.analyticsTab
                    );
                }
            },
            {searchDiagrams: [], productDiagrams: [], compareRangeActive: false}
        );

        const activeViews: AnalyticsTab[] = [];

        if (diagramInstruction.searchDiagrams.length > 0) {
            activeViews.push(AnalyticsTab.SearchView);
        }

        if (diagramInstruction.productDiagrams.length > 0) {
            activeViews.push(AnalyticsTab.ProductView);
        }

        return {
            tableInstruction: {
                activeViews,
                filters: [],
                searches: [],
                channels: [],
                sortings: [],
            },
            diagramInstruction,
        };
    };

    public static mergeTableInstructions = (memoryItems: Array<Partial<HistoryItem>>): MergedBuildingBlockInstructions => {
        const removeUndefined = (item?: any) => item !== undefined;
        return {
            tableInstruction: {
                activeViews: _.uniq(
                    memoryItems.reduce((activeViews: AnalyticsTab[], memoryItem) => {
                        if (memoryItem.analyticsTab) {
                            activeViews.push(memoryItem.analyticsTab);
                        }
                        return activeViews;
                    }, [])
                ),
                channels: _.uniq(
                    memoryItems.reduce((channels: string[], memoryItem) => {
                        if (memoryItem.channel) {
                            channels.push(memoryItem.channel);
                        }
                        return channels;
                    }, [])
                ),
                searches: _.uniq(
                    memoryItems.reduce((searches: string[], memoryItem) => {
                        if (memoryItem.activeSearch && memoryItem.activeSearch !== '') {
                            searches.push(memoryItem.activeSearch);
                        }
                        return searches;
                    }, [])
                ),
                sortings: _.uniqWith(memoryItems.map((memoryItem) => memoryItem.activeTableSort).filter(removeUndefined), _.isEqual),
                filters: _.uniqWith(
                    memoryItems.reduce(
                        (bundledFilters, memoryItem) => [...bundledFilters, ...toJS(memoryItem.activeFilters ? memoryItem.activeFilters : [])],
                        []
                    ),
                    _.isEqual
                ),
            },
        };
    };

    public getMergedBuildingBlockInstructions = (buildingBlock: ReportBuildingBlock): MergedBuildingBlockInstructions => {
        switch (buildingBlock.blockType) {
            case BuildingBlockType.TEXT:
                return {
                    textInstruction: {
                        text: buildingBlock.text,
                        textStyle: buildingBlock.textStyle,
                    },
                };
            case BuildingBlockType.DIAGRAM:
                return ReportEditorStore.mergeDiagramInstructions(buildingBlock.memoryItems);
            case BuildingBlockType.DIAGRAM_TABLE:
                return ReportEditorStore.mergeDiagramInstructions(buildingBlock.memoryItems);
            case BuildingBlockType.TABLE:
                return ReportEditorStore.mergeTableInstructions(buildingBlock.memoryItems);
            case BuildingBlockType.PAGE_BREAK:
                return {};
            default:
                return expectNever(buildingBlock);
        }
    };

    private static toSearchDiagramInstruction = (
        lineDiagramRequest: SerializedLineDiagramRequest | SerializedChannelDiagramRequest
    ): SearchDiagramInstruction => {
        if (isSerializedLineDiagramRequest(lineDiagramRequest)) {
            return {
                channel: lineDiagramRequest.channel,
                searchterm: lineDiagramRequest.searchtermOrProductId,
                kpi: lineDiagramRequest.kpi,
            };
        }
        return {
            channel: lineDiagramRequest.channel,
            kpi: lineDiagramRequest.kpi,
        };
    };

    private static toProductDiagramInstruction = (
        lineDiagramRequest: SerializedLineDiagramRequest | SerializedChannelDiagramRequest
    ): ProductDiagramInstruction => {
        if (isSerializedLineDiagramRequest(lineDiagramRequest)) {
            return {
                channel: lineDiagramRequest.channel,
                productId: lineDiagramRequest.searchtermOrProductId,
                kpi: lineDiagramRequest.kpi,
            };
        }
        return {
            channel: lineDiagramRequest.channel,
            kpi: lineDiagramRequest.kpi,
        };
    };

    private checkForReportErrors = () => {
        const {name, blocks, id} = this.reportEditing;
        const availableReports = this.reportStoreDependencies.getAvailableReports();
        const reportErrors: ReportError[] = [];
        if (name === '') {
            reportErrors.push(ReportError.ReportNameMissing);
        }

        const differentReportsWithSameName = availableReports.filter((report) => report.name === name && report.id !== id);

        if (differentReportsWithSameName.length > 0) {
            reportErrors.push(ReportError.ReportNameAlreadyUsed);
        }

        if (this.reportBuildingBlocksAreEmpty(blocks)) {
            reportErrors.push(ReportError.ReportEmpty);
        }
        if (this.hasSearchProductMixedBuildingBlocks(blocks)) {
            reportErrors.push(ReportError.ProductAndSearchDataMixed);
        }

        if (this.hasToManyChannels(blocks)) {
            reportErrors.push(ReportError.ToManyChannels);
        }

        if (this.hasToManySearches(blocks)) {
            reportErrors.push(ReportError.ToManySearches);
        }

        if (this.hasToManySortings(blocks)) {
            reportErrors.push(ReportError.ToManySortings);
        }

        this.reportEditing.errors = reportErrors;
    };

    public saveReport = (force = false) => {
        this.saveConflict = undefined;
        const {layout, namedDateRanges, id} = this.reportEditing;
        const availableReports = this.reportStoreDependencies.getAvailableReports();
        const matchingExistingReport = availableReports.find((availableReport) => availableReport.id === id);

        this.checkForReportErrors();

        if (this.reportEditing.errors.length > 0) {
            return;
        }

        const report = {
            layout,
            lastChanged: matchingExistingReport ? matchingExistingReport.lastChanged : 0,
            dates: namedDateRanges.map((namedDateRange) => ({
                id: namedDateRange.id,
                name: namedDateRange.name,
            })),
            variables: this.extractVariablesFromTextBuildingBlocks(this.reportEditing.blocks),
            currency: this.reportStoreDependencies.getTargetCurrency(),
            id: this.reportEditing.id,
            name: this.reportEditing.name,
            blocks: this.reportEditing.blocks,
        };

        this.isSaving = true;
        const savePromise = matchingExistingReport
            ? this.reportStoreDependencies.saveExistingReport(report, force)
            : this.reportStoreDependencies.saveNewReport(report);
        savePromise
            .then(() => {
                this.isSaving = false;
                this.closeReportEditor();
            })
            .catch((e) => {
                if (e.response.status === 409) {
                    this.saveConflict = {
                        changedOnDisk: true,
                        showRenameDialog: false,
                    };
                }
                this.isSaving = false;
            });
    };

    private applyDefaultVisibilityIfDefault = (
        reportBlock: TableBuildingBlock | DiagramTableBuildingBlock,
        newPageLayout: PageLayout,
        oldPageLayout: PageLayout
    ): ToggleableSelectorTableColumnDef[] => {
        const mergedTableData = this.getMergedBuildingBlockInstructions(reportBlock);

        if (!mergedTableData.tableInstruction) {
            throw Error('Target building block needs to have table instructions set for column visibility change');
        }

        if (mergedTableData.tableInstruction.activeViews.length > 1) {
            return reportBlock.columns;
        }

        const activeView = mergedTableData.tableInstruction.activeViews[0];
        const defaultOldVisibleColumns = this.VISIBLE_DEFAULT_COLUMNS[oldPageLayout][activeView];
        const currentVisibleDataKeys = reportBlock.columns.reduce((visibleDataKeys: DataKey[], column) => {
            if (column.isVisible) {
                visibleDataKeys.push(column.dataKey);
            }
            return visibleDataKeys;
        }, []);

        currentVisibleDataKeys.sort();
        defaultOldVisibleColumns.sort();

        if (_.isEqual(currentVisibleDataKeys, defaultOldVisibleColumns)) {
            const defaultNewVisibleColumns = this.VISIBLE_DEFAULT_COLUMNS[newPageLayout][activeView];
            return this.reportStoreDependencies.getTableColumnListWithChannelPerAnalyticsTab(activeView).map((column) => ({
                ...column,
                isVisible: defaultNewVisibleColumns.some((defaultDataKey) => defaultDataKey === column.dataKey),
            }));
        }

        return reportBlock.columns;
    };

    private changeBuildingBlockColumnsIfDefault = (newPageLayout: PageLayout) => {
        const reportBuildingBlocks = _.cloneDeep(toJS(this.reportEditing.blocks));
        this.reportEditing.blocks = reportBuildingBlocks.map((reportBlock) => {
            if (reportBlock.blockType === BuildingBlockType.DIAGRAM_TABLE || reportBlock.blockType === BuildingBlockType.TABLE) {
                return {
                    ...reportBlock,
                    columns: this.applyDefaultVisibilityIfDefault(reportBlock, newPageLayout, this.reportEditing.layout),
                };
            }
            return reportBlock;
        });
    };
}



// WEBPACK FOOTER //
// ./src/report/stores/ReportEditorStore.ts